import React, { useEffect, useRef, useState } from "react"
import ReactQuill from "react-quill"
import juice from "juice"
import { desktopToolbar, desktopToolbarHyperlink, mobileToolbar } from "../../config/quillToolbar"
import { richTextInputStyles } from "../../constants/rich_text_input_styles"
import { removeNonColorStyles } from "./Admin/Form/RichTextInput"
import { ChevronDownIcon } from "@heroicons/react/24/solid"
import Button from "./Button"

interface ComponentProps {
  id?: string
  defaultValue?: string
  value?: string
  setValue: (v: string) => void
  isDisabled?: boolean
  className?: string
  style?: React.CSSProperties
  hyperlink?: boolean
  setValueWithoutHtml?: (v: string) => void
  alwaysReloadQuillValue?: boolean
  setAlwaysReloadQuillValue?: React.Dispatch<React.SetStateAction<boolean>>
  dataTestId?: string
  optionalVariablesDropBox?: { value: string; label: string }[]
}

export const stripHtmlTags = (str: string) => str?.replace(/<\/?[^>]+(>|$)/g, "")

const RichTextInput = ({
  value,
  setValue,
  id,
  isDisabled,
  className,
  style,
  hyperlink,
  setValueWithoutHtml,
  alwaysReloadQuillValue,
  setAlwaysReloadQuillValue,
  dataTestId,
  optionalVariablesDropBox,
}: ComponentProps) => {
  const [isReady, setIsReady] = useState(false)
  const [quillValue, setQuillValue] = useState("")
  const [defaultQuillValue, setDefaultQuillValue] = useState<string | undefined>("")
  const [toolbarWidth, setToolbarWidth] = useState(0)
  const quillRef = useRef<ReactQuill | null>(null)

  const handleDropdownVariablesChange = (selectedValue: string) => {
    const quill = quillRef.current?.getEditor()

    if (quill) {
      const range = quill.getSelection()
      if (range) {
        quill.insertText(range.index, selectedValue + " ")
      } else {
        quill.insertText(quill.getLength(), selectedValue + " ")
      }
      setValue(quill.root.innerHTML)
      setValueWithoutHtml && setValueWithoutHtml(stripHtmlTags(quill.root.innerHTML))
    }
  }

  useEffect(() => {
    // Hacky solution to stop from autofocusing
    const timer = setTimeout(() => {
      setIsReady(true)
    }, 100)

    setQuillValue(removeNonColorStyles(value || ""))
    setDefaultQuillValue(value)

    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (defaultQuillValue === value) {
      alwaysReloadQuillValue && setAlwaysReloadQuillValue && setAlwaysReloadQuillValue(false)
    }
  }, [value])

  return (
    <div className="relative">
      <ReactQuill
        data-testid={dataTestId}
        theme="snow"
        modules={{
          toolbar: window.innerWidth < 550 ? mobileToolbar : hyperlink ? desktopToolbarHyperlink : desktopToolbar,
        }}
        ref={quillRef}
        className={className}
        style={style}
        readOnly={!isReady || isDisabled}
        value={quillValue}
        onChange={(v) => {
          setQuillValue(v)
          setValue(juice(richTextInputStyles + v))
          setValueWithoutHtml && setValueWithoutHtml(stripHtmlTags(v))
        }}
        id={id}
      />
      {optionalVariablesDropBox && (
        <VariableDropdown
          options={optionalVariablesDropBox}
          onSelect={handleDropdownVariablesChange}
          toolbarWidth={toolbarWidth}
        />
      )}
    </div>
  )
}

export default RichTextInput

const VariableDropdown = ({
  options,
  onSelect,
  toolbarWidth,
}: {
  options: { value: string; label: string }[]
  onSelect: (value: string) => void
  toolbarWidth: number
}) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="absolute top-1 right-1">
      <Button
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          setIsOpen(!isOpen)
        }}
        className="border text-black font-semibold text-xs px-3 py-[3px] rounded-md flex items-center hover:bg-gray-50 focus:outline-none"
      >
        Add Variable
        <ChevronDownIcon className="h-5 w-5 ml-1" />
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-50">
          {options.map((option) => (
            <button
              type="button"
              key={option.value}
              onClick={() => {
                onSelect(option.value)
                setIsOpen(false)
              }}
              className="block w-full text-left px-3 py-2 hover:bg-gray-100 focus:outline-none"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
